import clsx from "clsx";
import EntryGrid from "components/EntryGrid";
import debounce from "lodash/debounce";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { Seomatic } from "react-seomatic";

import { PLASMIC } from "@abnormal/plasmic-init";
import { Box } from "@chakra-ui/react";
import EntryCarouselSidebar from "@components/EntryCarouselSidebar";
import EntryFilter from "@components/EntryFilter";
import Message from "@components/Message";
import PageSections from "@components/PageSections";
import PageTitle from "@components/PageTitle";
import Pagination from "@components/Pagination";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import Layout from "@layouts/Layout";
import {
  PlasmicComponent,
  PlasmicRootProvider,
} from "@plasmicapp/loader-nextjs";

const BlogLayout = (page) => {
	const {
		pageEntry,
		posts = [],
		blogCategories,
		navigation,
		category,
		formData,
		pageCount,
    pageNumber,
    plasmicData,
    queryCache,
		searchQuery
	} = page;

	useSetHeaderTheme("dark");

	const router = useRouter();

	const [imageHeight, setImageHeight] = useState(100);

	const handleImageHeight = debounce((newImageHeight) => {
		setImageHeight((oldHeight) =>
			newImageHeight > 0 ? newImageHeight : oldHeight
		);
	}, 500);

	// Handle fetching status
	const [isFetching, setIsFetching] = useState(false);

	const handleSearch = (value) => {
		setIsFetching(true);

		router.push(`/blog/search?q=${value}`, undefined, {
			scroll: false
		});
	};

	useEffect(() => {
		const handleRouteChange = () => {
			setIsFetching(false);
		};

		router.events.on("routeChangeComplete", handleRouteChange);

		return () => {
			router.events.off("routeChangeComplete", handleRouteChange);
		};
	}, [router]);

	// When layout is used for categories, make sure correct seomatic data gets loaded
	let seomatic = pageEntry.seomatic;
	if (typeof category !== "undefined") {
		seomatic = category.seomatic;
	}

	return (
		<Layout navigation={navigation}>
			<Seomatic Head={Head} {...seomatic} />
			<Box
				backgroundImage={
					"https://files.abnormalsecurity.com/production/images/abstract/Abstract-Seafoam-Hills.jpg?w=1920&h=1080&auto=compress%2Cformat&fit=crop&dm=1674505008&s=dbbbcbd299e9ea3258f68e4156643fc1"
				}
				backgroundColor="purple-03"
				backgroundRepeat="no-repeat"
				backgroundPosition="center"
				textAlign="center"
				backgroundSize="cover"
				pb={7}
				mb={10}
			>
				<PageTitle summary={pageEntry.summary}>{pageEntry.title}</PageTitle>
			</Box>
			{pageEntry.featuredEntries.length > 0 &&
				pageNumber === 1 &&
				!category && (
					<Box maxW={1240} mb={5} px={5} mx={"auto"}>
						<h2 className="text-2xl mb-5  md:hidden">Featured Resources</h2>
						<EntryCarouselSidebar
							entries={pageEntry.featuredEntries.slice(0, 4)}
						/>
					</Box>
				)}

			<div
				className={clsx(
					"BlogLayout__filter",
					"max-w-screen-xl mx-auto",
					"mb-7.5"
				)}
			>
				<div className={clsx("BlogLayout__search", "mb-7.5 xl:mb-7.5")}>
					<EntryFilter
						categoryGroups={[
							{
								handle: "blogCategories",
								label: "Filter by category",
								categories: blogCategories
							}
						]}
						selectedCategory={category}
						isFetching={isFetching}
						handleSearch={handleSearch}
						initialValue={searchQuery}
					/>
				</div>
			</div>

			{posts.length > 0 && (
				<div className={clsx("mb-7.5")}>
					<EntryGrid
						entries={posts}
						indentMobile
						showDate
						thumbnailAspectRatio="square"
					/>
				</div>
			)}
			{posts.length === 0 && !!searchQuery && (
				<div className={clsx("mb-15 px-15")}>
					<Message message="No posts match your search criteria. Please try again." />
				</div>
			)}
			{posts.length > 0 && (
				<div className={clsx("BlogLayout__pagination", "pb-7.5")}>
					<Pagination
						pageCount={pageCount}
						activePage={pageNumber}
						basePath={!!category ? `/blog/category/${category.slug}` : "/blog"}
					/>
				</div>
			)}
			{pageEntry.footerModule?.length > 0 && (
				<div className="mt-7.5">
					<PageSections
						sections={pageEntry.footerModule[0].pageSections}
						isPageModule
					/>
				</div>
      )}
      <PlasmicRootProvider
        loader={PLASMIC}
        prefetchedData={plasmicData}
        prefetchedQueryData={queryCache}
        pageQuery={router.query}
      >
        <PlasmicComponent
          component="BladesDynamicDemoBlade"
        />
      </PlasmicRootProvider>
		</Layout>
	);
};

export default BlogLayout;
